import { QueryCache } from 'react-query';
import { MonitoredQueryClient } from './monitored-query-client';

export const queryCache = new QueryCache();

export const MAX_QUERY_RETRIES = 2;

// Browsers report connectivity failures as a TypeError with a browser-specific message and no status code.
const NETWORK_ERROR_MESSAGES = [
  'failed to fetch', // Chrome/Edge
  'networkerror when attempting to fetch resource', // Firefox
  'load failed', // Safari
  'network request failed',
];

function isInternetDisconnectedError(error: any) {
  // We have to account for HttpError quirks in here --
  // A status means we got a response back, so this is a protocol failure, not a connectivity one. Checking
  // this first also keeps us out of HttpError's `message`, which is the raw response body and could easily
  // contain one of the strings below.
  if (typeof error?.status === 'number') return false;
  // `http` doesn't rethrow the browser's TypeError, it wraps it in a new Error and hangs the original off
  // `cause` (see fetchData in @frontend/fetch), so the recognizable message is one level down.
  const message = String(error?.cause?.message ?? error?.message ?? '').toLowerCase();
  return NETWORK_ERROR_MESSAGES.some((networkMessage) => message.includes(networkMessage));
}

/**
 * Opt-in retry policy: retries network failures and 5xx responses, never 4xx, at most MAX_QUERY_RETRIES times.
 *
 * Intentionally not applied globally for now. Opt in per query:
 *   useQuery(key, fn, { retry: shouldRetryQuery(), retryDelay })
 *
 * Kept (and tested) so global retries can be re-enabled in one place later, by making it the
 * default on `queryClient`.
 */
export function shouldRetryQuery(maxRetries: number = MAX_QUERY_RETRIES) {
  return function (failureCount: number, error: any) {
    if (failureCount >= maxRetries) return false;
    if (isInternetDisconnectedError(error)) return true;
    // Only retry 5xx errors, not 4xx. By default react-query will retry 4xx errors.
    return error?.status >= 500;
  };
}

/**
Same as react-query's default (see https://github.com/TanStack/query/blob/main/packages/query-core/src/retryer.ts#L49),
but with +/-25% jitter added to mitigate thundering-herd retries. Example: 

failureCount | base delay | jittered range
0            | 1s         | 0.75s - 1.25s
1            | 2s         | 1.5s  - 2.5s
2            | 4s         | 3s    - 5s
*/
export function retryDelay(failureCount: number) {
  const baseDelay = Math.min(1000 * 2 ** failureCount, 30_000);
  return Math.floor(baseDelay * (0.75 + Math.random() * 0.5));
}

// Single shared QueryClient instance for the app. Import this directly instead of creating a new QueryClient.
export const queryClient = new MonitoredQueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      // No global retries, opt in per query with `retry: shouldRetryQuery(), retryDelay` (see shouldRetryQuery above)
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  },
});
